import axios from 'axios';

const AUTH_API = 'https://api.slace.com/core';
const REFRESH_MARGIN_MS = 30_000;

export interface LoginResponse {
  access_token: string;
  expires_in: number;
  token_type: string;
}

export const setAuthToken = (token: string | null) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

export const getToken = () => localStorage.getItem('token');

export const getTokenExp = () => localStorage.getItem('expiresAt');

export const tokenValid = () => {
  const exp = getTokenExp();
  return !!getToken() && !!exp && new Date(exp).getTime() > Date.now();
};

export const tokenExpiringSoon = () => {
  const exp = getTokenExp();
  return !exp || new Date(exp).getTime() - Date.now() < REFRESH_MARGIN_MS;
};

export const storeSession = (data: LoginResponse) => {
  localStorage.setItem('token', data.access_token);
  localStorage.setItem('expiresAt', new Date(Date.now() + data.expires_in * 1000).toISOString());
  setAuthToken(data.access_token);
  scheduleRefresh();
};

export const clearSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('expiresAt');
  setAuthToken(null);
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

// remember_me makes the API set the httpOnly RID refresh cookie;
// withCredentials is required for the browser to store it.
export const login = (username: string, password: string) =>
  axios.post<LoginResponse>(
    `${AUTH_API}/auth/login`,
    { username, password, remember_me: true },
    { withCredentials: true }
  );

// trusted_device makes the API set the TDV cookie (30 days) so future
// logins skip 2FA entirely.
export const verifyTfaCode = (token: string, code: string) =>
  axios.post<LoginResponse>(
    `${AUTH_API}/auth/tfa-code`,
    { token, code, trusted_device: true },
    { withCredentials: true }
  );

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

export const refreshToken = async (): Promise<boolean> => {
  try {
    const res = await axios.post<LoginResponse>(`${AUTH_API}/auth/refresh-token`, undefined, {
      withCredentials: true,
    });
    if (res.data?.access_token) {
      storeSession(res.data);
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

export const scheduleRefresh = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
  }
  const exp = getTokenExp();
  if (!exp) {
    return;
  }
  const delay = Math.max(new Date(exp).getTime() - Date.now() - REFRESH_MARGIN_MS, 0);
  refreshTimer = setTimeout(() => {
    refreshToken();
  }, delay);
};

let interceptorInstalled = false;

// On a 401 from the debug backend, refresh once and replay the request;
// bounce to login only if the refresh itself fails.
export const installAuthInterceptor = (onAuthFailure: () => void) => {
  if (interceptorInstalled) {
    return;
  }
  interceptorInstalled = true;

  axios.interceptors.response.use(
    (response) => response,
    async (error: any) => {
      const cfg = error.config;
      const isAuthCall = cfg?.url?.includes(`${AUTH_API}/auth/`);
      if (error.response?.status === 401 && cfg && !cfg._retried && !isAuthCall) {
        cfg._retried = true;
        if (await refreshToken()) {
          cfg.headers = { ...cfg.headers, Authorization: `Bearer ${getToken()}` };
          return axios(cfg);
        }
        clearSession();
        onAuthFailure();
      }
      return Promise.reject(error);
    }
  );
};
